/**
 * The slice of OpenAPI / JSON Schema the Docs page reads, and the helpers for
 * walking it.
 *
 * Deliberately partial: the page renders one filtered spec produced by our own
 * backend, not arbitrary third-party documents, so this handles the shapes
 * FastAPI emits and stops there.
 */

export interface Schema {
  $ref?: string;
  anyOf?: Schema[];
  type?: string;
  title?: string;
  description?: string;
  properties?: Record<string, Schema>;
  required?: string[];
  items?: Schema;
  additionalProperties?: Schema | boolean;
  example?: unknown;
  default?: unknown;
  enum?: unknown[];
  pattern?: string;
}

export interface MediaType {
  schema?: Schema;
}

export interface ResponseObject {
  description?: string;
  content?: Record<string, MediaType>;
}

export interface Parameter {
  name: string;
  in: string;
  required?: boolean;
  description?: string;
  schema?: Schema;
}

export interface Operation {
  summary?: string;
  description?: string;
  parameters?: Parameter[];
  requestBody?: { required?: boolean; content?: Record<string, MediaType> };
  responses?: Record<string, ResponseObject>;
  security?: Record<string, string[]>[];
  callbacks?: Record<string, Record<string, Record<string, Operation>>>;
}

export interface SecurityScheme {
  type?: string;
  in?: string;
  name?: string;
  scheme?: string;
  bearerFormat?: string;
  description?: string;
}

/**
 * A file the response only names — `hotspots.json`, say. Not an operation, so
 * the backend files it under `x-artifacts` rather than in `paths`; see
 * `shared.integration_docs.ArtifactReference`.
 */
export interface ArtifactEntry {
  title: string;
  /** The response field carrying its path. */
  namedBy: string;
  description: string;
  schema: Schema;
}

export interface OpenApiSpec {
  info?: { title?: string; version?: string; description?: string };
  paths?: Record<string, Record<string, Operation>>;
  components?: {
    schemas?: Record<string, Schema>;
    securitySchemes?: Record<string, SecurityScheme>;
  };
  'x-artifacts'?: ArtifactEntry[];
}

/** Follow `$ref` into `components.schemas`. */
export function deref(spec: OpenApiSpec, schema: Schema | undefined): Schema {
  let current = schema ?? {};
  for (let hops = 0; current.$ref && hops < 10; hops++) {
    const name = current.$ref.split('/').pop() ?? '';
    current = spec.components?.schemas?.[name] ?? {};
  }
  return current;
}

/**
 * Pydantic emits `Optional[T]` as `anyOf: [T, null]`. Collapse it back to `T`
 * plus a nullable flag, or the type column reads "anyOf" for most of the body.
 */
export function unwrapNullable(schema: Schema): { schema: Schema; nullable: boolean } {
  if (!schema.anyOf?.length) return { schema, nullable: false };
  const concrete = schema.anyOf.filter((option) => option.type !== 'null');
  const nullable = concrete.length !== schema.anyOf.length;
  if (concrete.length !== 1) return { schema, nullable };
  // The description and the default sit on the wrapper, not on the branch.
  return {
    schema: {
      ...concrete[0],
      description: schema.description ?? concrete[0].description,
      ...(schema.default !== undefined ? { default: schema.default } : {}),
    },
    nullable,
  };
}

/** The schema's own name, when it is a `$ref` into `components.schemas`. */
export function refName(schema: Schema | undefined): string | undefined {
  return schema?.$ref?.split('/').pop();
}

/**
 * The type on its own. Enum members used to be crammed in here, which read as
 * the type being `no_plate | bracket_only | keep_plate` rather than a string
 * with three permitted values; `enumOf` reports those separately now.
 */
export function typeLabel(spec: OpenApiSpec, schema: Schema): string {
  const named = refName(schema);
  const resolved = deref(spec, schema);

  if (resolved.properties) return named ? `object · ${named}` : 'object';

  const values = resolved.additionalProperties;
  if (values && typeof values === 'object') {
    return `map of ${refName(values) ?? typeLabel(spec, values)}`;
  }
  if (resolved.type === 'array') {
    const items = resolved.items ?? {};
    return `array of ${refName(items) ?? typeLabel(spec, items)}`;
  }
  return resolved.type ?? 'any';
}

/** The permitted values, when the schema pins them down. */
export function enumOf(spec: OpenApiSpec, schema: Schema): string[] | undefined {
  const resolved = deref(spec, schema);
  return resolved.enum?.length ? resolved.enum.map(String) : undefined;
}

/**
 * `Field(default=…)` lands on the property, while a `$ref` sends the type
 * elsewhere — so the plate modes' `no_plate` default lives on the property and
 * the permitted values on the schema it points at. Reading only the resolved
 * schema silently loses those defaults.
 */
export function defaultOf(spec: OpenApiSpec, schema: Schema): unknown {
  if (schema.default !== undefined) return schema.default;
  return deref(spec, schema).default;
}

/** A `pattern` constraint, wherever it sits. */
export function patternOf(spec: OpenApiSpec, schema: Schema): string | undefined {
  return schema.pattern ?? deref(spec, schema).pattern;
}

export interface Field {
  name: string;
  schema: Schema;
  required: boolean;
  nullable: boolean;
}

/** The rows to draw for one object schema, required fields first. */
export function fieldsOf(spec: OpenApiSpec, schema: Schema): Field[] {
  const resolved = deref(spec, schema);

  if (resolved.properties) {
    const required = new Set(resolved.required ?? []);
    const fields = Object.entries(resolved.properties).map(([name, raw]) => {
      const { schema: inner, nullable } = unwrapNullable(raw);
      return { name, schema: inner, required: required.has(name), nullable };
    });
    return [...fields.filter((f) => f.required), ...fields.filter((f) => !f.required)];
  }

  // A free-form map (`Dict[str, Model]`) has no property names, so it gets one
  // row standing for whatever key the caller finds there.
  const values = resolved.additionalProperties;
  if (values && typeof values === 'object') {
    const { schema: inner, nullable } = unwrapNullable(values);
    return [{ name: 'any key', schema: inner, required: false, nullable }];
  }

  if (resolved.type === 'array' && resolved.items) {
    const { schema: inner, nullable } = unwrapNullable(resolved.items);
    return fieldsOf(spec, inner).length
      ? [{ name: 'each item', schema: inner, required: false, nullable }]
      : [];
  }

  return [];
}

/**
 * A payload to show when the schema carries no `example` of its own — the 422
 * body, mainly, which nobody hand-writes an example for.
 */
export function sampleFromSchema(spec: OpenApiSpec, schema: Schema, depth = 0): unknown {
  const { schema: resolved } = unwrapNullable(deref(spec, schema));

  if (resolved.example !== undefined) return resolved.example;
  if (resolved.default !== undefined) return resolved.default;
  if (resolved.enum?.length) return resolved.enum[0];
  if (depth > 5) return null;

  if (resolved.properties) {
    const out: Record<string, unknown> = {};
    for (const [name, property] of Object.entries(resolved.properties)) {
      out[name] = sampleFromSchema(spec, property, depth + 1);
    }
    return out;
  }
  if (resolved.additionalProperties && typeof resolved.additionalProperties === 'object') {
    return { 'any key': sampleFromSchema(spec, resolved.additionalProperties, depth + 1) };
  }
  if (resolved.type === 'array') {
    return [sampleFromSchema(spec, resolved.items ?? {}, depth + 1)];
  }

  switch (resolved.type) {
    case 'string':
      return 'string';
    case 'integer':
    case 'number':
      return 0;
    case 'boolean':
      return false;
    default:
      return null;
  }
}

/** The JSON body schema of a request or response, if it has one. */
export function jsonSchemaOf(
  carrier: { content?: Record<string, MediaType> } | undefined,
): Schema | undefined {
  return carrier?.content?.['application/json']?.schema;
}

export interface OperationEntry {
  path: string;
  method: string;
  operation: Operation;
}

/**
 * Every operation in the spec, in declaration order.
 *
 * That order is the backend's: each app lists the operations its integration
 * surface consists of, and the spec is generated from exactly those routes in
 * exactly that sequence. So the page can lean on it — the submit comes before
 * the poll because the app said so — instead of sorting by path and hoping.
 */
export function operationsOf(spec: OpenApiSpec): OperationEntry[] {
  const entries: OperationEntry[] = [];
  for (const [path, byMethod] of Object.entries(spec.paths ?? {})) {
    for (const [method, operation] of Object.entries(byMethod)) {
      entries.push({ path, method: method.toLowerCase(), operation });
    }
  }
  return entries;
}

/**
 * The callback declared on an operation, flattened. OpenAPI nests these by
 * callback name, then by the runtime expression naming the target URL, then by
 * method; for our purposes there is exactly one of each.
 */
export function callbackOf(
  operation: Operation | undefined,
): { target: string; operation: Operation } | undefined {
  for (const byTarget of Object.values(operation?.callbacks ?? {})) {
    for (const [target, byMethod] of Object.entries(byTarget)) {
      for (const method of ['post', 'put', 'patch']) {
        const inner = byMethod[method];
        if (inner) return { target, operation: inner };
      }
    }
  }
  return undefined;
}

/**
 * Split a Markdown document on its `###` headings.
 *
 * The page places some of these sections deliberately (Authentication gets its
 * own topic, the result-paths notes sit with the response) and shows whatever is
 * left over as further topics, so renaming a heading in the backend loses
 * placement but never loses the text.
 */
export function splitSections(markdown: string): { title: string; body: string }[] {
  const sections: { title: string; body: string }[] = [];
  let title = '';
  let body: string[] = [];

  const flush = () => {
    const text = body.join('\n').trim();
    if (title || text) sections.push({ title, body: text });
    body = [];
  };

  for (const line of (markdown ?? '').split('\n')) {
    const heading = /^###\s+(.*)$/.exec(line);
    if (heading) {
      flush();
      title = heading[1].trim();
    } else {
      body.push(line);
    }
  }
  flush();

  return sections;
}
